// encryptionUtils.js
import config from '../Config';

/**
 * Convert base64 string to Uint8Array
 */
function base64ToArrayBuffer(base64) {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Import encryption key from base64 string
 */
async function importKey(base64Key) {
  const keyData = base64ToArrayBuffer(base64Key);

  return await window.crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'AES-CBC', length: 256 },
    false,
    ['decrypt']
  );
}

/**
 * Decrypt data using AES-CBC
 * @param {Object} encryptedData - Object containing CipherText and IV
 * @param {string} encryptedData.CipherText - Base64 encoded cipher text
 * @param {string} encryptedData.IV - Base64 encoded initialization vector
 * @returns {Promise<Object>} Decrypted and parsed JSON object
 */
export async function decryptData(encryptedData) {
  try {
    // Get encryption key from config
    const encryptionKey = getEncryptionKey();

    if (!encryptionKey) {
      throw new Error('Encryption key not configured');
    }

    const key = await importKey(encryptionKey);
    const iv = base64ToArrayBuffer(encryptedData.IV);
    const cipherText = base64ToArrayBuffer(encryptedData.CipherText);

    const decrypted = await window.crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: iv,
      },
      key,
      cipherText
    );

    const decoder = new TextDecoder();
    const decryptedText = decoder.decode(decrypted);

    return JSON.parse(decryptedText);
  } catch (error) {
    //console.error('Decryption failed:', error);

    if (error.message.includes('not configured')) {
      throw error;
    }

    throw new Error(
      'Failed to decrypt data. The data may be corrupted or the encryption key may be invalid.'
    );
  }
}

/**
 * Get encryption key from configuration
 * @returns {string} Base64 encoded encryption key
 */
export function getEncryptionKey() {
  // Priority 1: From config file
  const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;
  if (encryptionKey) {
    return encryptionKey;
  }

  // Priority 2: From window global (if injected by server)
  if (window.__ENCRYPTION_KEY__) {
    return window.__ENCRYPTION_KEY__;
  }

  // Priority 3: From session storage (if fetched via API)
  const sessionKey = sessionStorage.getItem('__enc_key__');
  if (sessionKey) {
    return sessionKey;
  }

  // console.error('Encryption key not found in any configuration source');
  return null;
}

/**
 * Validate that encryption key is properly configured
 * @returns {boolean} True if key is valid
 */
export function validateEncryptionKey() {
  const key = getEncryptionKey();

  if (!key) {
    //console.error('Encryption key is not configured');
    return false;
  }

  // Check if key is base64 encoded and approximately correct length
  // A 256-bit key should be 44 characters when base64 encoded
  if (key.length < 40 || key.length > 48) {
    // console.error('Encryption key appears to be invalid (incorrect length)');
    return false;
  }

  // Test base64 decoding
  try {
    base64ToArrayBuffer(key);
    return true;
  } catch (error) {
    //console.error('Encryption key is not valid base64:', error);
    return false;
  }
}
